/**
 * 毎月の定額売上の読み書き(2026-10-23 段階3)。計算は fixedRevenue.ts。
 *  - ひな形: settings/fixed_revenues の items(書けるのは管理者だけ=権限ルールの settings)
 *  - 計上: 取引の ID を決めて作る。作る前に、その ID の取引がすでにあるかを1件ずつ確かめ、あるものは作らない
 *    (確かめるのに全取引を読む権限が要るため、計上できるのは経理・管理者)
 */
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { writeAuditLog } from '@core/lib/auditLog';
import type { Account, MonthStr } from '@core/lib/types';
import { createDirectEntries } from '@core/transactions/revenueRepo';
import { postingsForMonth, validateFixedRevenue, type FixedRevenue } from './fixedRevenue';

const REF = () => doc(getDb(), 'settings', 'fixed_revenues');

export async function fetchFixedRevenues(): Promise<FixedRevenue[]> {
  const snap = await getDoc(REF());
  const items = (snap.data()?.items as FixedRevenue[] | undefined) ?? [];
  return [...items].sort((a, b) => (a.id < b.id ? -1 : 1));
}

export async function saveFixedRevenues(items: FixedRevenue[], userId: string): Promise<void> {
  for (const i of items) {
    const invalid = validateFixedRevenue(i);
    if (invalid) throw new Error(`${i.name || i.id}: ${invalid}`);
  }
  if (new Set(items.map((i) => i.id)).size !== items.length) throw new Error('ID が重なっています');
  await setDoc(REF(), { items, updated_by: userId, updated_at: serverTimestamp() });
  await writeAuditLog({ targetType: 'master', targetId: 'fixed_revenues', action: 'fixed-revenues-save', by: userId, after: { count: items.length } });
}

/** その月の計上状況(作る予定のもの・すでに作ったもの) */
export async function fixedRevenueStatus(
  items: FixedRevenue[],
  month: MonthStr,
  vendorName: (id: string) => string,
): Promise<Array<ReturnType<typeof postingsForMonth>[number] & { posted: boolean }>> {
  const postings = postingsForMonth(items, month, vendorName);
  const db = getDb();
  const posted = await Promise.all(postings.map((p) => getDoc(doc(db, 'transactions', p.id)).then((s) => s.exists())));
  return postings.map((p, i) => ({ ...p, posted: posted[i]! }));
}

/** まだ作っていないものだけ計上する。戻り値は作った件数 */
export async function postFixedRevenues(params: {
  items: FixedRevenue[];
  month: MonthStr;
  vendorName: (id: string) => string;
  accounts: Account[];
  userId: string;
  fiscalStartMonth: number;
}): Promise<{ created: number; skipped: number; error: string | null }> {
  const status = await fixedRevenueStatus(params.items, params.month, params.vendorName);
  const todo = status.filter((s) => !s.posted);
  if (todo.length === 0) return { created: 0, skipped: status.length, error: null };
  const ids = todo.map((t) => t.id);
  const result = await createDirectEntries({
    inputs: todo.map((t) => t.input),
    accounts: params.accounts,
    userId: params.userId,
    fiscalStartMonth: params.fiscalStartMonth,
    action: 'fixed-revenue-post',
    idOf: (_input, i) => ids[i]!,
  });
  return { created: result.created, skipped: status.length - todo.length, error: result.error };
}
